import { schemaResolver, useForm } from '@mantine/form';
import { z } from 'zod';
import {
  Alert,
  Button,
  Group,
  Modal,
  Radio,
  Select,
  Stack,
  TextInput,
} from '@mantine/core';
import { POLICY_TYPE_OPTIONS } from '#/service/driver-management/constants/fieldOptions.ts';
import { INSURER_OPTIONS } from '#/service/driver-management/constants/insurers.ts';
import { notifySuccess } from '#/utils/NotifySuccess.tsx';
import { getDisplayErrorMessage } from '#/service/driver-management/utils/apiError.ts';
import { useCreateInsuranceExpiryReminder } from '../hooks/useInsuranceExpiryReminders.ts';
import { RocDateInput } from '#/service/driver-management/components/RocDateInput.tsx';
import {
  requiredDate,
  requiredText,
} from '#/service/driver-management/utils/formValidation.ts';
import {
  InsurancePolicyTypeSchema,
  type InsurancePolicyType,
} from '#/service/driver-management/model.ts';
import type { DateStringValue } from '@mantine/dates';

type FormValues = {
  plateNumber: string;
  policyType: InsurancePolicyType;
  expiryDate: DateStringValue | null;
  insurer: string;
};

type CreateInsuranceModalProps = {
  opened: boolean;
  accountId: string;
  onClose: () => void;
};

const CreateInsuranceFormSchema = z.object({
  plateNumber: requiredText('請輸入車牌號碼'),
  policyType: z.enum(InsurancePolicyTypeSchema.options, {
    error: '請選擇險種',
  }),
  expiryDate: requiredDate('請選擇車險到期日'),
  insurer: requiredText('請選擇保險公司'),
});

const CreateInsuranceModal = ({
  opened,
  accountId,
  onClose,
}: CreateInsuranceModalProps) => {
  const { mutate, isPending, error, reset } =
    useCreateInsuranceExpiryReminder();
  const form = useForm<FormValues, z.infer<typeof CreateInsuranceFormSchema>>({
    initialValues: {
      plateNumber: '',
      policyType: InsurancePolicyTypeSchema.options[0],
      expiryDate: null,
      insurer: '',
    },
    validate: schemaResolver(CreateInsuranceFormSchema, { sync: true }),
    transformValues: (values) => CreateInsuranceFormSchema.parse(values),
  });

  const handleClose = () => {
    form.reset();
    reset();
    onClose();
  };

  const handleSubmit = (values: z.infer<typeof CreateInsuranceFormSchema>) => {
    mutate(
      { accountId, ...values },
      {
        onSuccess: () => {
          notifySuccess('已新增保單');
          handleClose();
        },
      },
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      closeOnClickOutside={!isPending}
      closeOnEscape={!isPending}
      withCloseButton={!isPending}
      title="新增保單"
      centered
      styles={{ title: { fontWeight: 700, fontSize: 16 } }}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label="車牌號碼"
            placeholder="請輸入車牌號碼"
            withAsterisk
            {...form.getInputProps('plateNumber')}
          />
          <Radio.Group
            label="險種"
            withAsterisk
            {...form.getInputProps('policyType')}
          >
            <Group mt="xs">
              {POLICY_TYPE_OPTIONS.map(({ value, label }) => (
                <Radio key={value} value={value} label={label} />
              ))}
            </Group>
          </Radio.Group>
          <RocDateInput
            label="車險到期日"
            placeholder="請選擇車險到期日"
            withAsterisk
            {...form.getInputProps('expiryDate')}
          />
          <Select
            label="保險公司"
            placeholder="請選擇保險公司"
            data={INSURER_OPTIONS}
            searchable
            withAsterisk
            {...form.getInputProps('insurer')}
          />
          {error && (
            <Alert color="red" variant="light">
              新增失敗：{getDisplayErrorMessage(error)}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={handleClose}
              disabled={isPending}
            >
              取消
            </Button>
            <Button type="submit" loading={isPending}>
              確認
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export { CreateInsuranceModal };
